import React, { useState } from 'react';
import { Card, Row, Col } from '@/components/ui';
import { Project } from '../../../types/Project';
import { formatDollars } from '../../../utils/formatDollars';

interface ProjectEarnedRevenueProps {
  project: Project;
}

const ProjectEarnedRevenue: React.FC<ProjectEarnedRevenueProps> = ({ project }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Use aggregated totals from backend (calculated from ALL tasks, not just paginated ones)
  const defaultCosts = {
    vehicle: 0,
    material: 0,
    labour: 0,
    subcontractor: 0,
    hiredEquipment: 0,
    other: 0,
    total: 0
  };

  const earnedRevenue = project.aggregatedTotals?.earnedRevenue || defaultCosts;
  const baseCosts = project.aggregatedTotals?.baseCosts || defaultCosts;

  // Calculate markups for display
  const markups = {
    vehicle: (project.vehicleMarkup || 0) / 100,
    material: (project.materialMarkup || 0) / 100,
    labour: (project.labourMarkup || 0) / 100,
    subcontractor: (project.subcontractorMarkup || 0) / 100,
    hiredEquipment: (project.hiredEquipmentMarkup || 0) / 100
  };

  const getPercentage = (amount: number) => {
    return earnedRevenue.total > 0 ? ((amount / earnedRevenue.total) * 100).toFixed(1) + '%' : '0%';
  };

  const formatMarkup = (markup: number) => {
    return `+${(markup * 100).toFixed(1)}%`;
  };

  return (
    <Row>
      {/* Vehicle Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">
              Vehicle
              {markups.vehicle > 0 && (
                <small className="tw-text-success"> ({formatMarkup(markups.vehicle)})</small>
              )}
            </h6>
            <p className="tw-mb-0">
              {formatDollars(earnedRevenue.vehicle)}
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Material Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">
              Material
              {markups.material > 0 && (
                <small className="tw-text-success"> ({formatMarkup(markups.material)})</small>
              )}
            </h6>
            <p className="tw-mb-0">
              {formatDollars(earnedRevenue.material)}
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Labour Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">
              Labour
              {markups.labour > 0 && (
                <small className="tw-text-success"> ({formatMarkup(markups.labour)})</small>
              )}
            </h6>
            <p className="tw-mb-0">
              {formatDollars(earnedRevenue.labour)}
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Subcontractor Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">
              Subcontractor
              {markups.subcontractor > 0 && (
                <small className="tw-text-success"> ({formatMarkup(markups.subcontractor)})</small>
              )}
            </h6>
            <p className="tw-mb-0">
              {formatDollars(earnedRevenue.subcontractor)}
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Hired Equipment Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">
              Hired Equipment
              {markups.hiredEquipment > 0 && (
                <small className="tw-text-success"> ({formatMarkup(markups.hiredEquipment)})</small>
              )}
            </h6>
            <p className="tw-mb-0">
              {formatDollars(earnedRevenue.hiredEquipment)}
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Other Costs Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">Other Costs</h6>
            <p className="tw-mb-0">
              {formatDollars(earnedRevenue.other)}
            </p>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default ProjectEarnedRevenue; 